/**
 * RepQuota.tsx — Per-AE quota attainment view.
 * Route: /#/reports/rep-quota
 *
 * Reads live Supabase data from v_ae_pipeline_summary and sales_plans.
 * Palette: Mi6 black #0B0B0F · red #DC2626.
 */
import { useEffect, useMemo, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import {
  UserRow,
  PipelineSummaryRow,
  SalesPlanRow,
  fmtUSD,
  listAllUsers,
  listPipelineSummary,
  listSalesPlans,
} from "@/lib/reporting-live";
import { getHealthColor, HEALTH_PALETTE } from "@shared/schema";

const MI6_BLACK  = "#0B0B0F";
const MI6_RED   = "#DC2626";
const MI6_SLATE = "#6B6B70";
const MI6_GREEN = HEALTH_PALETTE.darkGreen;
const MI6_YELLOW = "#EAB308";
const MI6_GRAY  = "#6B7280";

// Quota attainment % is score-driven — canon palette per CANON.md §5.
function attainmentColor(pct: number): string {
  return getHealthColor(pct);
}

interface RepRow {
  user: UserRow;
  quota: number;
  won: number;
  weighted: number;
  forecast: number;
  attainmentPct: number;
  coverageMultiple: number;
}

export default function RepQuota() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [pipeline, setPipeline] = useState<PipelineSummaryRow[]>([]);
  const [plans, setPlans] = useState<SalesPlanRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [us, ps, pl] = await Promise.all([
          listAllUsers(),
          listPipelineSummary(),
          listSalesPlans(),
        ]);
        if (cancelled) return;
        setUsers(us);
        setPipeline(ps);
        setPlans(pl);
        setLoading(false);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message || "Failed to load");
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const rows: RepRow[] = useMemo(() => {
    const aes = users.filter((u) => u.role === "ae" || u.role === "bdr");

    const planByAe = new Map<number, SalesPlanRow>();
    plans.forEach((p) => planByAe.set(p.ae_user_id, p));

    const pipeByAe = new Map<number, PipelineSummaryRow>();
    pipeline.forEach((p) => pipeByAe.set(p.ae_user_id, p));

    return aes.map((u) => {
      const plan = planByAe.get(u.id);
      const ps   = pipeByAe.get(u.id);
      const quota    = Number(plan?.quota_annual || ps?.quota_annual || 0);
      const won      = Number(ps?.won_value || 0);
      const weighted = Number(ps?.weighted_pipeline || 0);
      const forecast = won + weighted;
      const attainmentPct = quota > 0 ? Math.round((won / quota) * 100) : 0;
      const coverageMultiple = quota > 0 ? forecast / quota : 0;
      return { user: u, quota, won, weighted, forecast, attainmentPct, coverageMultiple };
    }).sort((a, b) => b.attainmentPct - a.attainmentPct);
  }, [users, pipeline, plans]);

  const totalQuota = rows.reduce((s, r) => s + r.quota, 0);
  const totalWon   = rows.reduce((s, r) => s + r.won, 0);
  const teamAttainment = totalQuota > 0 ? Math.round((totalWon / totalQuota) * 100) : 0;

  return (
    <OperatorShell breadcrumbs={[{ label: "Rep Quota" }]} pageTitle="Rep Quota" pageMeta="Reports">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 flex-1 w-full">
        <a
          href="#/dashboards/executive"
          className="text-xs font-medium"
          style={{ color: MI6_SLATE }}
        >
          ← Executive Dashboard
        </a>

        <div className="mt-4 mb-6">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1" style={{ color: MI6_SLATE }}>
            Mi6 Attilius · Reports
          </div>
          <h1 className="text-2xl font-extrabold" style={{ color: MI6_BLACK }}>
            Per-AE Quota Attainment
          </h1>
          <p className="text-sm mt-1" style={{ color: MI6_SLATE }}>
            2026 annual quota vs booked ARR — ranked by attainment
          </p>
        </div>

        {/* Team summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4 border-l-4" style={{ borderLeftColor: MI6_BLACK }}>
            <div className="text-[9px] uppercase tracking-[0.18em] font-bold mb-1" style={{ color: MI6_SLATE }}>Team Quota</div>
            <div className="text-xl font-extrabold tabular-nums" style={{ color: MI6_BLACK }}>{fmtUSD(totalQuota)}</div>
            <div className="text-xs mt-0.5" style={{ color: MI6_SLATE }}>{rows.length} reps</div>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4 border-l-4" style={{ borderLeftColor: MI6_GREEN }}>
            <div className="text-[9px] uppercase tracking-[0.18em] font-bold mb-1" style={{ color: MI6_SLATE }}>Team Booked</div>
            <div className="text-xl font-extrabold tabular-nums" style={{ color: MI6_GREEN }}>{fmtUSD(totalWon)}</div>
            <div className="text-xs mt-0.5" style={{ color: MI6_SLATE }}>won YTD</div>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4 border-l-4" style={{ borderLeftColor: attainmentColor(teamAttainment) }}>
            <div className="text-[9px] uppercase tracking-[0.18em] font-bold mb-1" style={{ color: MI6_SLATE }}>Team Attainment</div>
            <div className="text-xl font-extrabold tabular-nums" style={{ color: attainmentColor(teamAttainment) }}>
              {teamAttainment}%
            </div>
            <div className="text-xs mt-0.5" style={{ color: MI6_SLATE }}>green at 95%</div>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-neutral-200 h-64 animate-pulse" />
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm" style={{ color: "#991B1B" }}>
            {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-neutral-300 p-8 text-center text-sm" style={{ color: MI6_SLATE }}>
            No aes found in this tenant.
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  {["Rank", "Rep", "Role", "Quota", "Booked", "Weighted Pipe", "Attainment", "Coverage"].map((h) => (
                    <th key={h} className="px-4 py-2.5 text-left font-semibold" style={{ color: MI6_SLATE }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const color = attainmentColor(r.attainmentPct);
                  return (
                    <tr key={r.user.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                      <td className="px-4 py-3 tabular-nums text-sm" style={{ color: MI6_SLATE }}>{i + 1}</td>
                      <td className="px-4 py-3">
                        <a
                          href={`#/reporting/rep/${encodeURIComponent(String(r.user.id))}`}
                          className="font-semibold hover:underline"
                          style={{ color: MI6_BLACK }}
                        >
                          {r.user.name || r.user.full_name || r.user.email.split("@")[0]}
                        </a>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider"
                          style={{ background: "#EFEFEE", color: MI6_SLATE }}
                        >
                          {r.user.role.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 tabular-nums" style={{ color: MI6_SLATE }}>
                        {r.quota > 0 ? fmtUSD(r.quota) : "—"}
                      </td>
                      <td className="px-4 py-3 tabular-nums font-semibold" style={{ color: MI6_BLACK }}>
                        {fmtUSD(r.won)}
                      </td>
                      <td className="px-4 py-3 tabular-nums" style={{ color: MI6_SLATE }}>
                        {fmtUSD(r.weighted)}
                      </td>
                      <td className="px-4 py-3">
                        {r.quota > 0 ? (
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 w-20 rounded-full bg-neutral-100 overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{ width: `${Math.min(r.attainmentPct, 100)}%`, background: color }}
                              />
                            </div>
                            <span className="font-bold tabular-nums" style={{ color }}>{r.attainmentPct}%</span>
                          </div>
                        ) : (
                          <span style={{ color: MI6_SLATE }}>—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 tabular-nums font-semibold" style={{
                        // No quota (e.g. BDR rows) → not applicable, em-dash in slate.
                        // Quota set but nothing booked yet → gray, not red: red implies
                        // failing, but with no bookings this is just early in the period.
                        // Otherwise apply the coverage thresholds.
                        color:
                          r.quota <= 0 ? MI6_SLATE
                          : r.won <= 0 ? MI6_GRAY
                          : r.coverageMultiple >= 3 ? MI6_GREEN
                          : r.coverageMultiple >= 2 ? MI6_YELLOW
                          : MI6_RED,
                      }}>
                        {r.quota > 0 ? `${r.coverageMultiple.toFixed(1)}x` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </OperatorShell>
  );
}
